/**
 * ObjectGrid React component
 * 
 * @author    Jens Gruschel
 * @copyright © 2018 contexagon GmbH
 */


// react
import React from 'react';
import PropTypes from 'prop-types';
import {connect} from "react-redux";

// material UI
import {withStyles} from '@material-ui/core/styles';
import GridList from '@material-ui/core/GridList';
import GridListTile from '@material-ui/core/GridListTile';
import Fab from "@material-ui/core/Fab/Fab";

// icons
import AddIcon from '@material-ui/icons/Add';

// application components
import ObjectCard from './ObjectCard';
import PageSelection from "../base/PageSelection";

// application
import {getCardImageUrl, getMediaUrl} from '../../operations/utils';
import {selectTranslation} from "../../redux/configuration/selectors";


const columnWidth = 320;
const defaultColumnCount = 3;


/**
 * ObjectGrid shows a list of object cards in a grid
 *
 * @author Jens Gruschel
 */
export class ObjectGrid extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            columnCount: defaultColumnCount,
            overallWidth: defaultColumnCount * columnWidth,
            pageIndex: 0
        }
    }

    updateDimensions() {
        if (this.divElement) {
            const divWidth = this.divElement.clientWidth;
            let columnCount = Math.floor(divWidth / columnWidth);
            if (columnCount < 1) columnCount = 1;
            const overallWidth = columnWidth * columnCount;
            // console.log("ObjectGrid updateDimensions:", divWidth, columnWidth, columnCount, overallWidth);
            this.setState({columnCount: columnCount, overallWidth: overallWidth});
        }
    }

    componentDidMount() {
        this.updateDimensions();
        window.addEventListener("resize", this.updateDimensions.bind(this));
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.updateDimensions.bind(this));
    }

    render() {

        const renderAddButton = () => {
            if (this.props.onAdd && !this.props.readOnly) {
                return <div style={{width: 310, height: 330, margin: 3, backgroundColor: 'rgba(0,0,0,0.1)'}}>
                    <Fab color="primary" aria-label="Add" className={classes.addButton} onClick={this.props.onAdd}>
                        <AddIcon/>
                    </Fab>
                </div>;
            }
            else return undefined;
        };

        const { classes } = this.props;

        const itemsPerPage = this.state.columnCount * 4;
        const pageCount = this.props.pagination ? Math.ceil(this.props.items.length / itemsPerPage) : 1;
        const pageIndex = this.props.pagination ? Math.min(this.state.pageIndex, pageCount - 1) : 0;

        const items = this.props.pagination ? this.props.items.slice(pageIndex * itemsPerPage, (pageIndex + 1) * itemsPerPage) : this.props.items;

        return <div>
            <div className={classes.root} ref={(divElement) => this.divElement = divElement}>
                <GridList cellHeight={320} className={classes.gridList} cols={this.state.columnCount} style={{'width': this.state.overallWidth}}>
                    {items.map((item, index) => {
                        const accessInfo = item.accessInfo || {};
                        const itemAvailable = !!item.type; // all items need a type - if no type is present, the item is not available at all (maybe because of missing privileges)
                        const itemDeleted = !!item['timestamp-deleted'];
                        const listReadonly = this.props.readOnly;
                        const itemReadonly = listReadonly || !accessInfo.writable;
                        const itemDeleteable = accessInfo.deletable;                
                        const accessRestricted = item.objectaccess &&
                                                 item.objectaccess.accessibility &&
                                                 item.objectaccess.accessibility !== "writable";

                        return <div key={item.id || index} style={{marginBottom: 20, padding: 0}}>
                            <GridListTile cols={1} style={{height: 340}}>
                                <ObjectCard
                                    id={item.id}
                                    type={item.type}
                                    title={this.props.translate(item.title)}
                                    description={this.props.translate(item.description)}
                                    thumbnailurl={getCardImageUrl(item, this.props.typeDefinitions)}
                                    mediaurl={getMediaUrl(item)}
                                    tags={item.tags}
                                    mark={item.todo ? "sms_failed" : accessRestricted ? "lock" : item.remarks ? "chat" : null}
                                    onSelect={itemAvailable ? () => this.props.onSelect(index, item) : null}
                                    onEdit={this.props.onEdit && itemAvailable && !itemDeleted ? () => this.props.onEdit(index, item) : null}
                                    onRemove={this.props.onRemove && !listReadonly ? () => this.props.onRemove(index, item) : null}
                                    onDelete={this.props.onDelete && !itemDeleted && itemDeleteable ? () => this.props.onDelete(index, item) : null}
                                    onUndelete={this.props.onUndelete && itemDeleted && !itemReadonly ? () => this.props.onUndelete(index, item) : null}
                                    onDuplicate={this.props.onDuplicate && itemAvailable && !itemDeleted && !listReadonly ? () => this.props.onDuplicate(index, item) : null}
                                    onMoveToFront={this.props.onMoveToFront && !listReadonly && index > 0 ? () => this.props.onMoveToFront(index) : null}
                                    onSearchTag={this.props.onSearchTag}
                                />
                            </GridListTile>
                        </div>
                    })}
                    {renderAddButton()}
                </GridList>
            </div>
            {this.props.pagination ? <PageSelection pageNumber={pageIndex + 1} pageCount={pageCount} onChange={(number) => this.setState({pageIndex: number - 1})}/> : null}
        </div>;
    }
}

ObjectGrid.propTypes = {
    items: PropTypes.array.isRequired,
    readOnly: PropTypes.bool,
    onSelect: PropTypes.func.isRequired,
    onEdit: PropTypes.func,
    onDelete: PropTypes.func,
    onUndelete: PropTypes.func,
    onDuplicate: PropTypes.func,
    onMoveToFront: PropTypes.func,
    onSearchTag: PropTypes.func,
    pagination: PropTypes.bool
};

const styles = theme => ({
    root: {
        display: 'flex',
        flexWrap: 'wrap',
        overflow: 'hidden',
    },
    gridList: {
        textAlign: "center"
    },
    addButton: {
        marginTop: 125,
    }
});

const mapStateToProps = (state) => {
    return {
        typeDefinitions: state.types.typeDefinitions,
        authorization: state.authorization,
        configuration: state.configuration,
        translate: (value) => selectTranslation(state.configuration, value)
    };
};

export default connect(mapStateToProps)(withStyles(styles)(ObjectGrid));